import { isStudioThemeId, STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import {
  KIT_ASSET_WORKING_STATUSES,
  KitAssetStage,
  KitAssetStatus,
  SerializedKitAsset,
} from '../../../../redux/api/fymAPI.types';
import { ReelStatus, SerializedReel } from '../types';
import { isFullVideoAsset, RenderParts, renderPartsOf } from '../studio/fullVideoProgress';

export type WorkState = 'working' | 'ready' | 'failed';

interface WorkRowBase {
  /** Unique on the page: `asset:<uuid>` or `reel:<uuid>`. */
  key: string;
  uuid: string;
  state: WorkState;
  /** The step of a working row; null when the row is ready or failed. */
  stage: KitAssetStage | null;
  /** 4 for a video that reads the song and for the AI reel; 3 for the others (design r2). */
  stepCount: 3 | 4;
  /** 0..100: the render progress. Only the render step shows it. */
  progress: number;
  startedAt: number;
  /** Milliseconds; null while the row works. */
  finishedAt: number | null;
  /** A full video that renders: the part in progress and the parts total (design s10); else null. */
  parts: RenderParts | null;
  /** A full video that works: the time left in seconds; else null. */
  etaSec: number | null;
  /**
   * A queued full video: the type of the full video it waits for (one full video of the artist
   * renders at a time, design s10); else null.
   */
  waitsFor: StudioThemeId | null;
  /** A preview render of the Free plan or the trial: the row shows the "Free plan preview" note. */
  preview: boolean;
}

export type WorkRow =
  | (WorkRowBase & { kind: 'asset'; asset: SerializedKitAsset })
  | (WorkRowBase & { kind: 'reel'; reel: SerializedReel });

const REEL_WORKING: ReadonlyArray<ReelStatus> = ['queued', 'analysing', 'planning', 'rendering'];

const REEL_STAGE: Partial<Record<ReelStatus, KitAssetStage>> = {
  queued: 'queued',
  analysing: 'song',
  planning: 'plan',
  rendering: 'render',
};

const ASSET_STAGE_FALLBACK: Partial<Record<KitAssetStatus, KitAssetStage>> = {
  queued: 'queued',
  planning: 'plan',
  rendering: 'render',
};

const time = (iso: string | null | undefined): number | null => {
  if (iso === null || iso === undefined) {
    return null;
  }
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
};

function readsSong(asset: SerializedKitAsset): boolean {
  return isStudioThemeId(asset.themeId) && STUDIO_THEMES[asset.themeId].needsMaster;
}

const isWorkingAsset = (asset: SerializedKitAsset): boolean => KIT_ASSET_WORKING_STATUSES.includes(asset.status);

/**
 * The full video that a queued full video waits for (design s10): another current full video that
 * plans or renders, else an older queued one (it goes first). Undefined for any other asset.
 */
export function fullVideoBlocker(
  asset: SerializedKitAsset,
  assets: ReadonlyArray<SerializedKitAsset>
): SerializedKitAsset | undefined {
  if (asset.status !== 'queued' || !isFullVideoAsset(asset)) {
    return undefined;
  }
  const others = assets.filter(
    (other) =>
      other.uuid !== asset.uuid && other.supersededAt === null && isFullVideoAsset(other) && isWorkingAsset(other)
  );
  const moving = others.find((other) => other.status !== 'queued');
  if (moving !== undefined) {
    return moving;
  }
  return others
    .filter((other) => other.createdAt < asset.createdAt)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))[0];
}

/**
 * The row of a kit asset, whatever its state. `siblings` are the other assets of the song: a
 * queued full video reads them for "Waits for your lyric video".
 */
export function assetWorkRow(asset: SerializedKitAsset, siblings: ReadonlyArray<SerializedKitAsset> = []): WorkRow {
  const isWorking = isWorkingAsset(asset);
  const state: WorkState = isWorking ? 'working' : asset.status === 'failed' ? 'failed' : 'ready';
  const startedAt = time(asset.createdAt) ?? 0;
  const isFull = isFullVideoAsset(asset);
  const finishedAt = isWorking ? null : (time(asset.finishedAt) ?? time(asset.renderedAt) ?? time(asset.updatedAt));
  return {
    kind: 'asset',
    asset,
    key: `asset:${asset.uuid}`,
    uuid: asset.uuid,
    state,
    stage: isWorking ? (asset.stage ?? ASSET_STAGE_FALLBACK[asset.status] ?? 'queued') : null,
    stepCount: readsSong(asset) ? 4 : 3,
    progress: asset.status === 'ready' ? 100 : asset.progress,
    startedAt,
    finishedAt: isWorking ? null : (finishedAt ?? startedAt),
    parts: renderPartsOf(asset),
    etaSec: isWorking && isFull ? (asset.etaSec ?? null) : null,
    waitsFor: fullVideoBlocker(asset, siblings)?.themeId ?? null,
    preview: asset.preview === true,
  };
}

/** The row of a reel. A rejected or approved reel has no row. */
export function reelWorkRow(reel: SerializedReel): WorkRow | null {
  const isWorking = REEL_WORKING.includes(reel.status);
  if (!isWorking && reel.status !== 'ready' && reel.status !== 'failed') {
    return null;
  }
  const startedAt = time(reel.createdAt) ?? 0;
  return {
    kind: 'reel',
    reel,
    key: `reel:${reel.uuid}`,
    uuid: reel.uuid,
    state: isWorking ? 'working' : reel.status === 'failed' ? 'failed' : 'ready',
    stage: isWorking ? (REEL_STAGE[reel.status] ?? 'queued') : null,
    stepCount: 4,
    progress: reel.status === 'ready' ? 100 : reel.progress,
    startedAt,
    finishedAt: isWorking ? null : (time(reel.updatedAt) ?? startedAt),
    parts: null,
    etaSec: null,
    waitsFor: null,
    preview: reel.preview === true,
  };
}

/** The kit assets and reels of a song that are being made (the "2 working" pill of the Assets tab, r7). */
export function countWorking(assets: SerializedKitAsset[], reels: SerializedReel[]): number {
  return (
    assets.filter((asset) => asset.supersededAt === null && KIT_ASSET_WORKING_STATUSES.includes(asset.status)).length +
    reels.filter((reel) => REEL_WORKING.includes(reel.status)).length
  );
}
